<template>
  <div class="musubi-page musubi-page--domain-providers">
    <header class="musubi-page__head">
      <h1>Domain providers</h1>
      <p class="text-muted">
        Credentials for the platform's OWN Enom (registrar), Cloudflare (DNS) and Mailgun
        (email) accounts — used by the buy-through-us domain flow and email delivery. Enter
        them here instead of pasting them into env vars; secrets are encrypted at rest and
        never shown back in full.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <MusubiSectionCard title="Enom — domain registrar" icon="globe">
        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">Environment</span>
            <span v-if="state.enom.overrides.environment" class="dp-field__hint">override active</span>
          </div>
          <select v-model="form.enom_environment" class="form-select form-select-sm dp-select">
            <option value="sandbox">Sandbox (resellertest.enom.com)</option>
            <option value="production">Production (reseller.enom.com)</option>
          </select>
          <p class="dp-field__help" v-if="form.enom_environment === 'sandbox'">
            Sandbox — orders placed here don't register real domains. Switch to Production
            only once credentials + the egress-IP allowlist (below) are confirmed.
          </p>
          <p class="dp-field__help" v-else>
            Production — orders placed here register REAL domains against the live Enom account.
          </p>
        </div>

        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">Reseller UID</span>
            <span v-if="clears.enom_uid" class="dp-field__hint dp-field__hint--clear">will clear on save</span>
            <span v-else-if="state.enom.overrides.uid" class="dp-field__hint">set</span>
          </div>
          <div class="dp-field__row">
            <input
              v-model="form.enom_uid"
              type="password"
              class="form-control form-control-sm"
              autocomplete="new-password"
              :disabled="clears.enom_uid"
              :placeholder="state.enom.uid_masked || 'reseller account UID'"
            >
            <button v-if="state.enom.overrides.uid" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('enom_uid')">
              {{ clears.enom_uid ? 'Undo' : 'Clear' }}
            </button>
          </div>
        </div>

        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">API token</span>
            <span v-if="clears.enom_api_token" class="dp-field__hint dp-field__hint--clear">will clear on save</span>
            <span v-else-if="state.enom.overrides.api_token" class="dp-field__hint">set</span>
          </div>
          <div class="dp-field__row">
            <input
              v-model="form.enom_api_token"
              type="password"
              class="form-control form-control-sm"
              autocomplete="new-password"
              :disabled="clears.enom_api_token"
              :placeholder="state.enom.api_token_masked || 'reseller API token'"
            >
            <button v-if="state.enom.overrides.api_token" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('enom_api_token')">
              {{ clears.enom_api_token ? 'Undo' : 'Clear' }}
            </button>
          </div>
          <p class="dp-field__help">
            Leave both fields blank to keep the current credentials. Use <strong>Clear</strong> to remove one.
          </p>
        </div>

        <h4 class="dp-subhead">Registrant contact</h4>
        <p class="text-muted small dp-subhead__help">
          Managed domains are registered in OUR Enom account, so this is OUR custody contact —
          Enom rejects a purchase without complete registrant fields.
        </p>
        <div class="dp-grid">
          <label class="dp-field">
            <span class="dp-field__label">First name</span>
            <input v-model="form.enom_registrant_first" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Last name</span>
            <input v-model="form.enom_registrant_last" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field dp-field--wide">
            <span class="dp-field__label">Organisation</span>
            <input v-model="form.enom_registrant_org" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field dp-field--wide">
            <span class="dp-field__label">Address line 1</span>
            <input v-model="form.enom_registrant_address1" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">City</span>
            <input v-model="form.enom_registrant_city" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Postcode</span>
            <input v-model="form.enom_registrant_postcode" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Country (ISO-2)</span>
            <input v-model="form.enom_registrant_country" type="text" maxlength="2" class="form-control form-control-sm" placeholder="GB" style="text-transform:uppercase">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Email</span>
            <input v-model="form.enom_registrant_email" type="email" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Phone</span>
            <input v-model="form.enom_registrant_phone" type="text" class="form-control form-control-sm" placeholder="+44.2071234567">
          </label>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard title="Cloudflare — DNS" icon="server" class="dp-card-gap">
        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">API token</span>
            <span v-if="clears.cloudflare_api_token" class="dp-field__hint dp-field__hint--clear">will clear on save</span>
            <span v-else-if="state.cloudflare.overrides.api_token" class="dp-field__hint">set</span>
          </div>
          <div class="dp-field__row">
            <input
              v-model="form.cloudflare_api_token"
              type="password"
              class="form-control form-control-sm"
              autocomplete="new-password"
              :disabled="clears.cloudflare_api_token"
              :placeholder="state.cloudflare.api_token_masked || 'CF bearer token'"
            >
            <button v-if="state.cloudflare.overrides.api_token" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('cloudflare_api_token')">
              {{ clears.cloudflare_api_token ? 'Undo' : 'Clear' }}
            </button>
          </div>
          <p class="dp-field__help">
            A scoped API token (Zone:Edit, DNS:Edit) — not the legacy Global API Key. Blank keeps
            the current token; use <strong>Clear</strong> to remove it.
          </p>
        </div>

        <div class="dp-grid">
          <label class="dp-field">
            <span class="dp-field__label">Account ID</span>
            <input v-model="form.cloudflare_account_id" type="text" class="form-control form-control-sm">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Nameserver 1</span>
            <input v-model="form.cloudflare_ns1" type="text" class="form-control form-control-sm" placeholder="ada.ns.cloudflare.com">
          </label>
          <label class="dp-field">
            <span class="dp-field__label">Nameserver 2</span>
            <input v-model="form.cloudflare_ns2" type="text" class="form-control form-control-sm" placeholder="milo.ns.cloudflare.com">
          </label>
        </div>
        <p class="dp-field__help">
          The nameserver pair Cloudflare assigned to this account — freshly-registered domains get
          delegated to these. Find them on the Cloudflare dashboard (or a zone's overview page).
        </p>
      </MusubiSectionCard>

      <MusubiSectionCard title="Mailgun — email delivery" icon="envelope" class="dp-card-gap">
        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">API key</span>
            <span v-if="clears.mailgun_api_key" class="dp-field__hint dp-field__hint--clear">will clear on save</span>
            <span v-else-if="state.mailgun.env_api_key_set && !state.mailgun.overrides.api_key" class="dp-field__hint">env-supplied</span>
            <span v-else-if="state.mailgun.overrides.api_key" class="dp-field__hint">set</span>
          </div>
          <div class="dp-field__row">
            <input
              v-model="form.mailgun_api_key"
              type="password"
              class="form-control form-control-sm"
              autocomplete="new-password"
              :disabled="clears.mailgun_api_key"
              :placeholder="state.mailgun.api_key_masked || 'key-...'"
            >
            <button v-if="state.mailgun.overrides.api_key" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('mailgun_api_key')">
              {{ clears.mailgun_api_key ? 'Undo' : 'Clear' }}
            </button>
          </div>
          <p class="dp-field__help">
            Used for sending, webhooks and domain management. Domain management requires a key with domain scope.
            You can save the key on its own — no sending domain required. Blank keeps the current key; use
            <strong>Clear</strong> to remove the override and fall back to env.
          </p>
        </div>

        <div class="dp-field">
          <div class="dp-field__head">
            <span class="dp-field__label">Webhook signing key</span>
            <span v-if="clears.mailgun_webhook_key" class="dp-field__hint dp-field__hint--clear">will clear on save</span>
            <span v-else-if="state.mailgun.env_webhook_key_set && !state.mailgun.overrides.webhook_key" class="dp-field__hint">env-supplied</span>
            <span v-else-if="state.mailgun.overrides.webhook_key" class="dp-field__hint">set</span>
          </div>
          <div class="dp-field__row">
            <input
              v-model="form.mailgun_webhook_key"
              type="password"
              class="form-control form-control-sm"
              autocomplete="new-password"
              :disabled="clears.mailgun_webhook_key"
              :placeholder="state.mailgun.webhook_key_masked || 'signing key'"
            >
            <button v-if="state.mailgun.overrides.webhook_key" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('mailgun_webhook_key')">
              {{ clears.mailgun_webhook_key ? 'Undo' : 'Clear' }}
            </button>
          </div>
          <p class="dp-field__help">
            Separate from the API key — verifies incoming bounce / complaint / inbound-reply webhooks.
            Find it in Mailgun → Sending → Webhooks.
          </p>
        </div>
      </MusubiSectionCard>

      <div class="dp-actions">
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
          <span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['dp-actions__msg', saveOk ? 'is-ok' : 'is-error']">
          {{ saveMessage }}
        </div>
      </div>

      <MusubiSectionCard title="Reference" icon="info-circle" class="dp-card-gap" subtitle="Read-only — not editable here">
        <dl class="dp-info">
          <dt>Egress-IP allowlist</dt>
          <dd>
            <template v-if="state.info.egress_ips.length">
              <code v-for="ip in state.info.egress_ips" :key="ip" class="dp-ip">{{ ip }}</code>
            </template>
            <span v-else class="text-muted">none configured</span>
          </dd>
          <dt>Note</dt>
          <dd class="text-muted small">{{ state.info.egress_note }}</dd>
          <dt>Cloudflare nameservers (effective)</dt>
          <dd>
            <template v-if="state.info.cloudflare_nameservers_effective.length">
              <code v-for="ns in state.info.cloudflare_nameservers_effective" :key="ns" class="dp-ip">{{ ns }}</code>
            </template>
            <span v-else class="text-muted">none configured</span>
          </dd>
        </dl>
      </MusubiSectionCard>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/integrations/domain-providers';

const emptyForm = () => ({
  enom_environment: 'sandbox',
  enom_uid: '',
  enom_api_token: '',
  enom_registrant_first: '',
  enom_registrant_last: '',
  enom_registrant_org: '',
  enom_registrant_address1: '',
  enom_registrant_city: '',
  enom_registrant_postcode: '',
  enom_registrant_country: '',
  enom_registrant_email: '',
  enom_registrant_phone: '',
  cloudflare_api_token: '',
  cloudflare_account_id: '',
  cloudflare_ns1: '',
  cloudflare_ns2: '',
  mailgun_api_key: '',
  mailgun_webhook_key: '',
});

export default {
  name: 'AdminDomainProviders',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      state: {
        enom: {
          environment: 'sandbox',
          uid_masked: '', uid_set: false,
          api_token_masked: '', api_token_set: false,
          registrant: {},
          overrides: {},
        },
        cloudflare: {
          api_token_masked: '', api_token_set: false,
          account_id: '', ns1: '', ns2: '',
          overrides: {},
        },
        mailgun: {
          api_key_masked: '', api_key_set: false,
          webhook_key_masked: '', webhook_key_set: false,
          env_api_key_set: false, env_webhook_key_set: false,
          overrides: {},
        },
        info: { egress_ips: [], egress_note: '', cloudflare_nameservers_effective: [] },
      },
      form: emptyForm(),
      // Snapshot of the loaded values — plain fields diff against this so an
      // unchanged field is omitted from the PUT (never clobbers on partial save).
      originals: emptyForm(),
      // Secret fields staged for explicit removal (field-key => true). Sent as
      // the PUT's `clear` list; nothing else can remove a stored secret.
      clears: {},
      loading: true,
      saving: false,
      error: null,
      saveMessage: '',
      saveOk: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.state = {
          enom: { ...this.state.enom, ...(p.enom || {}) },
          cloudflare: { ...this.state.cloudflare, ...(p.cloudflare || {}) },
          mailgun: { ...this.state.mailgun, ...(p.mailgun || {}) },
          info: { ...this.state.info, ...(p.info || {}) },
        };
        const r = this.state.enom.registrant || {};
        this.form = {
          ...emptyForm(),
          enom_environment: this.state.enom.environment || 'sandbox',
          enom_registrant_first:    r.first_name || '',
          enom_registrant_last:     r.last_name  || '',
          enom_registrant_org:      r.org        || '',
          enom_registrant_address1: r.address1   || '',
          enom_registrant_city:     r.city       || '',
          enom_registrant_postcode: r.postcode   || '',
          enom_registrant_country:  r.country    || '',
          enom_registrant_email:    r.email      || '',
          enom_registrant_phone:    r.phone      || '',
          cloudflare_account_id: this.state.cloudflare.account_id || '',
          cloudflare_ns1:        this.state.cloudflare.ns1 || '',
          cloudflare_ns2:        this.state.cloudflare.ns2 || '',
          // Secret fields stay blank — user types a fresh value to update.
          enom_uid: '', enom_api_token: '', cloudflare_api_token: '',
        };
        // Reset the diff baseline + any staged clears to the freshly-loaded state.
        this.originals = { ...this.form };
        this.clears = {};
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load config.';
      } finally {
        this.loading = false;
      }
    },

    toggleClear(field) {
      // Stage/unstage an explicit clear for a secret field. Applied on Save via
      // the PUT's `clear` list — blanking a field can never clear it (blank = keep).
      this.clears = { ...this.clears, [field]: !this.clears[field] };
    },

    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;

      // NON-CLOBBERING PUT (papercut #42): only send what actually changed.
      //  - a field left untouched is OMITTED → the backend keeps its stored value;
      //  - a removal is an EXPLICIT entry in `clear`, never an empty string.
      const body = {};
      const clear = [];

      // Secrets: send only when the user typed a fresh value; a staged Clear (or
      // the legacy '(clear)' sentinel) removes the stored override.
      const secretFields = ['enom_uid', 'enom_api_token', 'cloudflare_api_token', 'mailgun_api_key', 'mailgun_webhook_key'];
      secretFields.forEach((key) => {
        if (this.clears[key]) { clear.push(key); return; }
        const v = this.form[key];
        if (v === '(clear)') { clear.push(key); return; }
        if (v) body[key] = v;
      });

      // Non-secrets (incl. the environment select): send only when CHANGED from
      // the loaded value. Emptying a previously-set field is an explicit clear.
      const plainFields = [
        'enom_environment',
        'enom_registrant_first', 'enom_registrant_last', 'enom_registrant_org',
        'enom_registrant_address1', 'enom_registrant_city', 'enom_registrant_postcode',
        'enom_registrant_country', 'enom_registrant_email', 'enom_registrant_phone',
        'cloudflare_account_id', 'cloudflare_ns1', 'cloudflare_ns2',
      ];
      plainFields.forEach((key) => {
        const cur = this.form[key];
        const orig = this.originals[key] ?? '';
        if (cur === orig) return;                 // unchanged → keep
        if (cur === '') { clear.push(key); return; } // emptied → explicit clear
        body[key] = cur;                          // changed → set
      });

      if (clear.length) body.clear = clear;

      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          await this.load();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.musubi-page--domain-providers { padding: var(--m-space-6, 32px); max-width: 760px; }

.dp-card-gap { margin-top: 18px; }

.dp-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.dp-field:last-child { margin-bottom: 0; }
.dp-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.dp-field__label { font-size: 0.85rem; font-weight: 500; color: var(--m-text-primary); }
.dp-field__hint {
  font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--m-text-tertiary);
}
.dp-field__hint--clear { color: var(--m-status-danger, #EF4444); }
.dp-field__row { display: flex; gap: 8px; align-items: center; }
.dp-field__row .form-control { flex: 1; }
.dp-field__help { font-size: 0.76rem; color: var(--m-text-secondary); margin: 2px 0 0; }
.dp-select { max-width: 320px; }

.dp-subhead { font-size: 0.82rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--m-text-tertiary); margin: 18px 0 2px; }
.dp-subhead__help { margin: 0 0 12px; }

.dp-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 16px;
}
.dp-field--wide { grid-column: 1 / -1; }

.dp-actions { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.dp-actions__msg { font-size: 0.85rem; }
.dp-actions__msg.is-ok { color: var(--m-status-success, #10B981); }
.dp-actions__msg.is-error { color: var(--m-status-danger, #EF4444); }

.dp-info { display: grid; grid-template-columns: 220px 1fr; gap: 8px 12px; font-size: 0.85rem; margin: 0; }
.dp-info dt { color: var(--m-text-secondary); }
.dp-info dd { margin: 0; }
.dp-ip {
  background: var(--m-bg-subtle, #F1F5F9); padding: 2px 6px; border-radius: 3px;
  font-size: 0.8rem; margin-right: 6px; display: inline-block;
}
</style>
